import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteCodelistDraft, listCodelistVersions } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { type CodelistVersion, type MappingKind } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { formatDateTime } from '../../utils/format';
import { BrokenMappingsPanel } from './BrokenMappingsPanel';
import { CodelistDiffDialog } from './CodelistDiffDialog';
import { CodelistVersionDialog } from './CodelistVersionDialog';
import { ImportCodelistDialog } from './ImportCodelistDialog';
import { versionStatusColors, versionStatusLabelKeys } from './codelistLabels';

/**
 * The tenant's versioned registries of standard codes, managed from within the one Codelists page
 * so the topic has a single home — for the kind selected above. A kind holds at most one active
 * version and at most one draft; the draft is imported, reviewed against the active version and
 * then activated, and all of it is this tenant's alone.
 */
export function CodelistVersionsSection({ kind }: { kind: MappingKind }) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [importing, setImporting] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [diffing, setDiffing] = useState<CodelistVersion | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CodelistVersion | null>(null);

  const query = useQuery({
    queryKey: queryKeys.codelists.versions(kind),
    queryFn: ({ signal }) => listCodelistVersions(kind, signal),
  });

  const deleteMutation = useMutation({
    mutationFn: (versionGid: string) => deleteCodelistDraft(versionGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.codelists.all });
      toastSuccess(t('codelists.versions.deleted'));
      setPendingDelete(null);
    },
    onError: (error) => {
      toastError(error);
      setPendingDelete(null);
    },
  });

  const versions = query.data ?? [];
  const activeVersion = versions.find((version) => version.status === 'Active') ?? null;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">{t('codelists.versions.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('codelists.versions.description')}
          </Typography>
        </Box>
        <Button startIcon={<UploadFileIcon />} variant="outlined" onClick={() => setImporting(true)}>
          {t('codelists.versions.import')}
        </Button>
      </Box>

      <Alert severity="info">{t('codelists.versions.note')}</Alert>

      {query.isPending && <LoadingBlock />}
      {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

      {query.isSuccess &&
        (versions.length === 0 ? (
          <EmptyBlock message={t('codelists.versions.empty')} />
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('codelists.versions.columns.versionLabel')}</TableCell>
                    <TableCell>{t('columns.status')}</TableCell>
                    <TableCell align="right">{t('codelists.versions.columns.entryCount')}</TableCell>
                    <TableCell>{t('codelists.versions.columns.activatedAt')}</TableCell>
                    <TableCell>{t('codelists.versions.columns.createdAt')}</TableCell>
                    <TableCell>{t('codelists.versions.columns.sourceNote')}</TableCell>
                    <TableCell align="right">{t('columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow key={version.versionGid} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{version.versionLabel}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant={version.status === 'Superseded' ? 'outlined' : 'filled'}
                          color={versionStatusColors[version.status]}
                          label={t(versionStatusLabelKeys[version.status])}
                        />
                      </TableCell>
                      <TableCell align="right">{version.entryCount}</TableCell>
                      <TableCell>{formatDateTime(version.activatedAt)}</TableCell>
                      <TableCell>{formatDateTime(version.createdAt)}</TableCell>
                      <TableCell>{version.sourceNote ?? '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('codelists.versions.actions.detail')}>
                          <IconButton size="small" onClick={() => setViewing(version.versionGid)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {version.status === 'Draft' && (
                          <>
                            <Tooltip title={t('codelists.versions.actions.diff')}>
                              <IconButton size="small" onClick={() => setDiffing(version)}>
                                <CompareArrowsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('codelists.versions.actions.deleteDraft')}>
                              <IconButton size="small" onClick={() => setPendingDelete(version)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}

      {query.isSuccess && <BrokenMappingsPanel kind={kind} activeVersion={activeVersion} />}

      {importing && <ImportCodelistDialog kind={kind} onClose={() => setImporting(false)} />}

      {viewing !== null && <CodelistVersionDialog versionGid={viewing} onClose={() => setViewing(null)} />}

      {diffing !== null && <CodelistDiffDialog version={diffing} onClose={() => setDiffing(null)} />}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('codelists.versions.confirmDeleteTitle')}
        message={t('codelists.versions.confirmDeleteMessage', { version: pendingDelete?.versionLabel ?? '' })}
        confirmLabel={t('actions.delete')}
        confirmColor="error"
        busy={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.versionGid)}
        onClose={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
